"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { ArrowUpRight, CircleHelp } from "lucide-react"
import { useTranslations } from "next-intl"
import { PublicNavGlass, type GlassNavMode } from "@/components/public-nav-glass"
import { PublicFooter } from "@/components/public-footer"
import { LocaleLink } from "@/components/locale-link"
import { GlassSegmentControl, GlassSurface, Reveal, displayHeadingStyle } from "@/components/glass/glass-kit"
import { JournalCategoryLabel } from "@/components/journal-category-label"
import { HelpPlate } from "@/components/help/help-plates"
import { BlogAuthorByline } from "@/components/blog/blog-author-byline"
import { BLOG_ARTICLES, getBlogCategoryLabel } from "@/lib/blog-articles"
import { isArticleVisible } from "@/lib/blog-visibility"
import { accentText } from "@/lib/journal-accent"
import { Lozenge } from "@/components/ui/lozenge"

const ENTRY_TITLE = "clamp(1.3rem, 2.7vw, 1.85rem)"

type BlogSortOrder = "newest" | "oldest"
type ContentFilter = "all" | "articles" | "guides"

function parseContentFilter(value: string | null): ContentFilter {
  if (value === "articles" || value === "journal") return "articles"
  if (value === "guides") return "guides"
  return "all"
}

function articleTimestamp(date: string) {
  const ms = Date.parse(date)
  return Number.isFinite(ms) ? ms : 0
}

function MastheadCount({
  filter,
  articleCount,
  guideCount,
  t,
}: {
  filter: ContentFilter
  articleCount: number
  guideCount: number
  t: ReturnType<typeof useTranslations<"pages.blog">>
}) {
  if (filter === "articles") {
    return (
      <span>
        {articleCount} {articleCount === 1 ? t("count.article") : t("count.articles")}
      </span>
    )
  }
  if (filter === "guides") {
    return (
      <span>
        {guideCount} {guideCount === 1 ? t("count.guide") : t("count.guides")}
      </span>
    )
  }
  return (
    <span>
      {articleCount} {articleCount === 1 ? t("count.article") : t("count.articles")}
      {" · "}
      {guideCount} {guideCount === 1 ? t("count.guide") : t("count.guides")}
    </span>
  )
}

function SectionHeading({
  id,
  title,
  hint,
  separated = false,
}: {
  id: string
  title: string
  hint?: string
  separated?: boolean
}) {
  return (
    <Reveal>
      <div
        id={id}
        className={separated ? "mt-12 border-t pt-8 sm:mt-14" : "pb-4"}
        style={separated ? { borderColor: "var(--pg-line)" } : undefined}
      >
        <h2 className="text-[15px] font-medium tracking-tight" style={{ color: "var(--pg-fg)" }}>
          {title}
        </h2>
        {hint ? (
          <p className="mt-1.5 max-w-lg text-[13px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
            {hint}
          </p>
        ) : null}
      </div>
    </Reveal>
  )
}

export default function BlogIndexClient() {
  const t = useTranslations("pages.blog")
  const router = useRouter()
  const searchParams = useSearchParams()
  const [mode, setMode] = useState<GlassNavMode>("dark")
  // The Newest/Oldest toggle was removed from the UI — articles always sort
  // newest-first now, but sortArticles below still takes a BlogSortOrder so
  // that logic didn't need touching.
  const sortOrder: BlogSortOrder = "newest"
  const [contentFilter, setContentFilter] = useState<ContentFilter>(() =>
    parseContentFilter(searchParams.get("type")),
  )

  useEffect(() => {
    setContentFilter(parseContentFilter(searchParams.get("type")))
  }, [searchParams])

  const filterOptions = useMemo(
    () =>
      [
        { value: "all" as const, label: t("filter.all") },
        { value: "articles" as const, label: t("filter.articles") },
        { value: "guides" as const, label: t("filter.guides") },
      ] as const,
    [t],
  )

  const sortArticles = useCallback(
    (items: typeof BLOG_ARTICLES) =>
      [...items].sort((a, b) => {
        const diff = articleTimestamp(b.date) - articleTimestamp(a.date)
        return sortOrder === "newest" ? diff : -diff
      }),
    [sortOrder],
  )

  const journalArticles = useMemo(() => {
    const items = BLOG_ARTICLES.filter((a) => isArticleVisible(a.slug) && a.kind !== "guide")
    return sortArticles(items)
  }, [sortArticles])

  const guideArticles = useMemo(() => {
    const items = BLOG_ARTICLES.filter((a) => isArticleVisible(a.slug) && a.kind === "guide")
    return sortArticles(items)
  }, [sortArticles])

  const showJournalList = contentFilter === "all" || contentFilter === "articles"
  const showHelpGuides = contentFilter === "all" || contentFilter === "guides"
  const showGuideArticles = showHelpGuides && guideArticles.length > 0
  const guidesStartIndex = journalArticles.length

  const showSectionLabels = contentFilter === "all" || contentFilter === "guides"
  const hasJournalSection = showJournalList && journalArticles.length > 0
  const hasHowToSection = showHelpGuides && guideArticles.length > 0

  const updateFilter = useCallback(
    (next: ContentFilter) => {
      setContentFilter(next)
      const params = new URLSearchParams(searchParams.toString())
      if (next === "all") params.delete("type")
      else params.set("type", next === "articles" ? "articles" : next)
      const query = params.toString()
      router.replace(query ? `/blog?${query}` : "/blog", { scroll: false })
    },
    [router, searchParams],
  )

  return (
    <GlassSurface mode={mode}>
      <PublicNavGlass mode={mode} onModeChange={setMode} />

      <main className="mx-auto w-full max-w-7xl px-4 pb-24 pt-28 sm:px-6 sm:pt-32 lg:px-8">
        <header>
          <Reveal>
            <div className="flex items-baseline justify-between gap-6">
              <h1 className="min-w-0 font-semibold" style={displayHeadingStyle()}>
                {t("hero.title")}
              </h1>
              <span className="shrink-0 text-[12.5px] tabular-nums" style={{ color: "var(--pg-muted)" }}>
                <MastheadCount
                  filter={contentFilter}
                  articleCount={journalArticles.length}
                  guideCount={guideArticles.length}
                  t={t}
                />
              </span>
            </div>

            <div className="mt-5 flex flex-col gap-5">
              <p className="max-w-md text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                {t("hero.subtitle")}
              </p>

              <div className="flex w-full flex-row items-center gap-3">
                <GlassSegmentControl
                  value={contentFilter}
                  onChange={updateFilter}
                  options={filterOptions}
                  ariaLabel={t("filter.label")}
                  className="min-w-0 shrink"
                />
              </div>
            </div>
          </Reveal>
        </header>

        <div className="mt-12 sm:mt-14">
          {showJournalList ? (
            <>
              {showSectionLabels && hasJournalSection ? (
                <SectionHeading
                  id="articles-section"
                  title={t("sections.articles")}
                  hint={t("sections.articlesHint")}
                />
              ) : null}

              {journalArticles.length === 0 ? (
                contentFilter === "articles" ? (
                  <Reveal>
                    <p className="text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                      {t("empty")}
                    </p>
                  </Reveal>
                ) : null
              ) : (
                journalArticles.map((article, i) => (
                  <Reveal key={article.slug} delay={i * 60}>
                    <LocaleLink
                      href={`/articles/${article.slug}`}
                      className="group block py-8 sm:py-9"
                      style={{ borderTop: i === 0 ? undefined : "1px solid var(--pg-line)" }}
                    >
                      <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-12">
                        <div className="min-w-0 flex-1">
                          <div
                            className="flex items-center gap-2.5 text-[12.5px]"
                            style={{ color: "var(--pg-muted)" }}
                          >
                            <span className="inline-flex items-center gap-1">
                              <Lozenge color={`rgb(${article.accent})`} />
                              <JournalCategoryLabel
                                label={getBlogCategoryLabel(article)}
                                style={{ color: accentText(article.accent) }}
                              />
                            </span>
                            <span aria-hidden style={{ opacity: 0.55 }}>
                              ·
                            </span>
                            <span className="tabular-nums">{article.date}</span>
                            <span aria-hidden style={{ opacity: 0.55 }}>
                              ·
                            </span>
                            <span>{String(i + 1).padStart(2, "0")}</span>
                          </div>

                          <h2
                            className="mt-3 font-medium transition-transform duration-500 md:group-hover:translate-x-2"
                            style={{ fontSize: ENTRY_TITLE, lineHeight: 1.18, letterSpacing: "-0.03em" }}
                          >
                            {article.title}
                          </h2>

                          <p
                            className="mt-3 max-w-lg text-[14px] leading-relaxed"
                            style={{ color: "var(--pg-muted)" }}
                          >
                            {article.excerpt}
                          </p>

                          <div className="mt-5">
                            <BlogAuthorByline
                              variant="compact"
                              date={article.date}
                              tone="glass"
                              authorKey={article.author}
                              showAvatar
                            />
                          </div>

                          <span
                            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium transition-opacity duration-300"
                            style={{ color: accentText(article.accent) }}
                          >
                            {t("readArticle")}
                            <ArrowUpRight
                              className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                              strokeWidth={1.75}
                            />
                          </span>
                        </div>

                        <div className="w-full shrink-0 md:w-1/2">
                          <HelpPlate
                            kind={article.plate}
                            figure={i + 1}
                            caption={article.plateCaption}
                            accent={article.accent}
                            className="w-full transition-transform duration-700 ease-out md:group-hover:-translate-y-2"
                          />
                        </div>
                      </div>
                    </LocaleLink>
                  </Reveal>
                ))
              )}
            </>
          ) : null}

          {showHelpGuides ? (
            <>
              {hasHowToSection ? (
                <>
                  {showSectionLabels ? (
                    <SectionHeading
                      id="howto-section"
                      title={t("sections.howTo")}
                      hint={t("sections.howToHint")}
                      separated={hasJournalSection}
                    />
                  ) : null}

                  {guideArticles.map((article, i) => (
                <Reveal key={article.slug} delay={(guidesStartIndex + i) * 60}>
                  <LocaleLink
                    href={`/articles/${article.slug}`}
                    className="group block py-8 sm:py-9"
                    style={{ borderTop: i > 0 ? "1px solid var(--pg-line)" : undefined }}
                  >
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-12">
                      <div className="min-w-0 flex-1">
                        <div
                          className="flex items-center gap-2.5 text-[12.5px]"
                          style={{ color: "var(--pg-muted)" }}
                        >
                          <span className="inline-flex items-center gap-1">
                            <Lozenge color={`rgb(${article.accent})`} />
                            <JournalCategoryLabel
                              label={getBlogCategoryLabel(article)}
                              style={{ color: accentText(article.accent) }}
                            />
                          </span>
                          <span aria-hidden style={{ opacity: 0.55 }}>
                            ·
                          </span>
                          <span className="tabular-nums">{article.date}</span>
                          <span aria-hidden style={{ opacity: 0.55 }}>
                            ·
                          </span>
                          <span>{String(i + 1).padStart(2, "0")}</span>
                        </div>

                        <h2
                          className="mt-3 font-medium transition-transform duration-500 md:group-hover:translate-x-2"
                          style={{ fontSize: ENTRY_TITLE, lineHeight: 1.18, letterSpacing: "-0.03em" }}
                        >
                          {article.title}
                        </h2>

                        <p className="mt-3 max-w-lg text-[14px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                          {article.excerpt}
                        </p>

                        <div className="mt-5">
                          <BlogAuthorByline
                            variant="compact"
                            date={article.date}
                            tone="glass"
                            authorKey={article.author}
                            showAvatar
                          />
                        </div>

                        <span
                          className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium transition-opacity duration-300"
                          style={{ color: accentText(article.accent) }}
                        >
                          {t("readArticle")}
                          <ArrowUpRight
                            className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            strokeWidth={1.75}
                          />
                        </span>
                      </div>

                      <div className="w-full shrink-0 md:w-1/2">
                        <HelpPlate
                          kind={article.plate}
                          figure={i + 1}
                          caption={article.plateCaption}
                          accent={article.accent}
                          className="w-full transition-transform duration-700 ease-out md:group-hover:-translate-y-2"
                        />
                      </div>
                    </div>
                  </LocaleLink>
                </Reveal>
                  ))}
                </>
              ) : null}
            </>
          ) : null}
        </div>

      </main>

      <a
        href="mailto:contact@boothapp.co"
        aria-label={t("support.cta")}
        className="fixed right-0 z-40 flex h-8 w-[2.125rem] items-center justify-center rounded-l-full transition-transform duration-300 hover:scale-[1.04] active:scale-[0.98]"
        style={{
          bottom: "5%",
          background: "var(--pg-invert-bg)",
          color: "var(--pg-invert-fg)",
          border: "1px solid var(--pg-card-edge)",
          borderRight: "none",
          boxShadow: "inset 0 1px 0 var(--pg-gloss), -8px 12px 40px -16px rgba(0,0,0,0.85)",
        }}
      >
        <CircleHelp className="h-4 w-4" strokeWidth={1.75} />
      </a>

      <PublicFooter />
    </GlassSurface>
  )
}
